'use client';

import { ComponentProps, useContext, useEffect, useMemo, useRef, useState } from 'react';
import defaultVariables from '@/config/styles/variables.json';
import { AppConfigContext, AppConfigProvider } from '@/features/app-providers/app-config-provider';
import { PageProvider } from '@/features/app-providers/page-provider';
import { WebsiteConfigProvider } from '@/features/app-providers/website-config-provider';
import { Config } from '@/schema/config-schema';
import defaultConfig from '@/schema/default-config.json';
import { localeToLang } from '@/shared/i18n/localeToLang';
import { resolveWebsiteLogos } from '@/shared/lib/resolve-website-logos';
import { IconConfigProvider } from '@/shared/ui/icon/icon-config';
import {
    type CustomPrefabs,
    type ProductDetailSettings,
    type ProductTagsSettings,
    prefabsStore,
} from '@/shared/ui/modal/prefabs-store';
import { getTemplateById } from '@/templates/registry';
import { PageComponentData, PrefabRegistry, PrefabRenderer } from '@/widgets/prefab-renderer';

type SocialLinks = ComponentProps<typeof AppConfigProvider>['socialLinks'];
type RendererPrefabs = ComponentProps<typeof PrefabRenderer>['customPrefabs'];

type PageConfig = {
    body?: Array<{ prefab?: string; component?: string; options?: Record<string, unknown> }>;
    metadata: {
        url?: Record<string, string | undefined>;
        title?: Record<string, string | undefined>;
        description?: Record<string, string | undefined>;
    };
};

type PreviewPageProps = {
    initialWebsite: Config;
    pageName: string;
    pageConfig: PageConfig;
    resolvedBody: unknown[];
    locale: string;
    pageParams: Record<string, string>;
};

export function PreviewPage({
    initialWebsite,
    pageName,
    pageConfig,
    resolvedBody,
    locale,
    pageParams,
}: PreviewPageProps) {
    const [website, setWebsite] = useState(initialWebsite);
    const [body, setBody] = useState(resolvedBody);
    const [configReady, setConfigReady] = useState(false);
    const [configError, setConfigError] = useState<string | null>(null);
    const fontStyleRef = useRef<HTMLStyleElement | null>(null);

    // Request current config from editor on mount (after navigation or initial load)
    useEffect(() => {
        window.parent.postMessage({ type: 'ordermage:config-request' }, window.location.origin);
    }, []);

    // Preserve ?preview=true across all in-iframe navigation
    useEffect(() => {
        function handleClick(e: MouseEvent) {
            const anchor = (e.target as HTMLElement).closest('a');
            if (!anchor?.href) return;
            try {
                const url = new URL(anchor.href);
                if (url.origin === window.location.origin) {
                    e.preventDefault();
                    e.stopPropagation();
                    url.searchParams.set('preview', 'true');
                    window.location.href = url.toString();
                }
            } catch {
                // ignore malformed URLs
            }
        }
        document.addEventListener('click', handleClick, true);
        return () => document.removeEventListener('click', handleClick, true);
    }, []);

    useEffect(() => {
        function handleMessage(event: MessageEvent) {
            if (event.origin !== window.location.origin) return;

            if (event.data?.type === 'ordermage:config-error') {
                if (!configReady) {
                    setConfigError(event.data.error as string);
                }
                return;
            }

            if (event.data?.type !== 'ordermage:config-update') return;
            const newConfig = event.data.config as Config;
            setWebsite(newConfig);

            // Keep prefabsStore in sync so ModalService uses current template/user prefabs
            const newTemplateId = newConfig.template as string | undefined;
            const newTemplatePrefabs = newTemplateId
                ? (getTemplateById(newTemplateId)?.template.prefabs as CustomPrefabs | undefined)
                : null;
            prefabsStore.setTemplatePrefabs(newTemplatePrefabs ?? null);
            prefabsStore.setPrefabs((newConfig.prefabs as CustomPrefabs | undefined) ?? {});
            prefabsStore.setProductDetail((newConfig.productDetail as ProductDetailSettings | null) ?? null);
            prefabsStore.setProductTags((newConfig.productTags as ProductTagsSettings | null) ?? null);

            // Re-resolve page body: user override → template → keep initial default
            const pages = newConfig.pages as Record<string, { body?: PageComponentData[] }> | undefined;
            const userBody = pages?.[pageName]?.body;
            if (userBody && userBody.length > 0) {
                setBody(userBody);
            } else {
                const tplId = newConfig.template as string | undefined;
                const tplPrefabs = tplId ? getTemplateById(tplId)?.template.prefabs : undefined;
                const prefabId = pageName
                    ? 'Page.' +
                      pageName
                          .split('-')
                          .map((s) => s[0].toUpperCase() + s.slice(1))
                          .join('')
                    : '';
                const tplPage = (
                    tplPrefabs as
                        | Record<string, { prefabs?: Record<string, { children?: unknown[] }> }>
                        | undefined
                )?.[prefabId];
                const tplChildren = tplPage?.prefabs?.default?.children;
                if (tplChildren && tplChildren.length > 0) {
                    setBody(tplChildren);
                } else {
                    // Fall back to default page prefab from registry (not initial resolvedBody which may be from old template)
                    const defaultPrefab = PrefabRegistry[prefabId as keyof typeof PrefabRegistry] as
                        | { prefabs?: Record<string, { children?: unknown[] }> }
                        | undefined;
                    const defaultChildren = defaultPrefab?.prefabs?.default?.children;
                    setBody(defaultChildren?.length ? defaultChildren : resolvedBody);
                }
            }

            // Update CSS variables (colors, radii, shadows, etc.)
            const templateId = newConfig.template as string | undefined;
            const templateColors = templateId ? (getTemplateById(templateId)?.template.colors ?? {}) : {};
            const styleVars = newConfig.styleVariables as Record<string, string> | undefined;
            const merged = { ...defaultVariables, ...templateColors, ...(styleVars ?? {}) };
            for (const [key, value] of Object.entries(merged)) {
                document.documentElement.style.setProperty(`--${key}`, value);
            }

            // Update font-face rules and font family CSS variables
            const templateFonts = templateId ? getTemplateById(templateId)?.template.fonts : undefined;
            const fonts = newConfig.fonts as
                | {
                      families?: { primary?: string; secondary?: string };
                      faces?: {
                          family: string;
                          src: string;
                          weight?: string | number;
                          style?: string;
                          display?: string;
                      }[];
                  }
                | undefined;

            // Build @font-face CSS — user faces override template faces
            const faces = (fonts?.faces?.length ?? 0) > 0 ? fonts!.faces! : (templateFonts?.faces ?? []);
            const css = faces
                .filter((f) => f.family && f.src)
                .map((f) => {
                    const src = f.src.trim().startsWith('url(') ? f.src.trim() : `url("${f.src.trim()}")`;
                    const weight = f.weight ? `font-weight: ${f.weight};` : '';
                    const style = f.style ? `font-style: ${f.style};` : '';
                    const display = f.display ? `font-display: ${f.display};` : '';
                    return `@font-face{font-family:"${f.family}";src:${src};${weight}${style}${display}}`;
                })
                .join('');

            if (!fontStyleRef.current) {
                fontStyleRef.current = document.createElement('style');
                document.head.appendChild(fontStyleRef.current);
            }
            fontStyleRef.current.textContent = css;

            const primaryFont = fonts?.families?.primary || templateFonts?.families?.primary;
            const secondaryFont = fonts?.families?.secondary || templateFonts?.families?.secondary;
            if (primaryFont) {
                document.body.style.setProperty('--font-primary', primaryFont);
            } else {
                document.body.style.removeProperty('--font-primary');
            }
            if (secondaryFont) {
                document.body.style.setProperty('--font-secondary', secondaryFont);
            } else {
                document.body.style.removeProperty('--font-secondary');
            }

            setConfigReady(true);
        }

        window.addEventListener('message', handleMessage);
        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [pageName, configReady]);

    useEffect(() => {
        return () => {
            if (fontStyleRef.current) {
                fontStyleRef.current.remove();
                fontStyleRef.current = null;
            }
        };
    }, []);

    const parentConfig = useContext(AppConfigContext);
    const lang = localeToLang(locale);
    const logos = useMemo(
        () => resolveWebsiteLogos(website, lang, parentConfig.venueLogoUrl ?? null),
        [website, lang, parentConfig.venueLogoUrl],
    );

    const templateId = (website as Record<string, unknown>)['template'] as string | undefined;
    const templatePrefabs = templateId
        ? (getTemplateById(templateId)?.template.prefabs as RendererPrefabs)
        : undefined;

    if (!configReady) {
        if (configError) {
            return (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100vh',
                        backgroundColor: '#0a0a0b',
                        color: '#9c9c9c',
                        fontFamily: 'system-ui, sans-serif',
                        gap: 12,
                    }}
                >
                    <div style={{ fontSize: 14 }}>Neplatná konfigurace</div>
                    <div style={{ fontSize: 12, color: '#666', maxWidth: 400, textAlign: 'center' }}>
                        {configError}
                    </div>
                </div>
            );
        }
        return (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100vh',
                    backgroundColor: '#0a0a0b',
                }}
            >
                <div
                    style={{
                        width: 32,
                        height: 32,
                        border: '3px solid #2a2b2e',
                        borderTopColor: '#0167F4',
                        borderRadius: '50%',
                        animation: 'spin 0.8s linear infinite',
                    }}
                />
                <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
            </div>
        );
    }

    return (
        <WebsiteConfigProvider website={website}>
            <AppConfigProvider
                logo={{ src: logos.top.src, alt: logos.top.alt }}
                footerLogo={{ src: logos.footer.src, alt: logos.footer.alt }}
                routes={parentConfig.routes}
                allRoutes={parentConfig.allRoutes}
                apiPartnerCode={parentConfig.apiPartnerCode}
                apiPartnerSettingsId={parentConfig.apiPartnerSettingsId}
                socialLinks={website.socialLinks as SocialLinks}
                appLinks={parentConfig.appLinks}
                venueLogoUrl={parentConfig.venueLogoUrl ?? null}
                verifyTelephoneEnabled={parentConfig.verifyTelephoneEnabled}
                // TODO: Remove — dočasná sleva na první objednávku (never applies in editor preview).
                firstOrderDiscountEnabled={false}
                termsAndConditions={
                    ((website as { termsAndConditions?: Record<string, string> }).termsAndConditions?.[
                        locale
                    ] as string | undefined) ??
                    parentConfig.termsAndConditions ??
                    ''
                }
                privacyPolicy={
                    ((website as { privacyPolicy?: Record<string, string> }).privacyPolicy?.[locale] as
                        | string
                        | undefined) ??
                    parentConfig.privacyPolicy ??
                    ''
                }
                venueRouter={null}
            >
                <IconConfigProvider icons={website.icons}>
                    <PageProvider
                        pageName={pageName}
                        route={pageConfig.metadata.url?.[locale] ?? null}
                        params={pageParams}
                    >
                        <PrefabRenderer
                            data={
                                (website.layout ??
                                    (defaultConfig as { layout?: PageComponentData[] }).layout ??
                                    body) as PageComponentData[]
                            }
                            contextOptions={website}
                            slots={{ body: body as PageComponentData[] }}
                            templatePrefabs={templatePrefabs}
                            customPrefabs={website.prefabs as RendererPrefabs}
                            isolate
                            preview
                        />
                    </PageProvider>
                </IconConfigProvider>
            </AppConfigProvider>
        </WebsiteConfigProvider>
    );
}
